import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import {
  AssignmentInd,
  School,
  WorkspacePremium
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import CrewTrainingUpdatesPanel from '../ops/CrewDutyRoster/components/CrewTrainingUpdatesPanel';
import TrainingPanel from './TrainingPanel';

const SECTION_CERTS = 0;
const SECTION_TRAININGS = 1;
const SECTION_ASSIGNMENTS = 2;

function sectionFromParam(value, showAssignments) {
  const v = String(value || '').toLowerCase();
  // Legacy HrmsSuite: trainingSubTab 0 = certs, 1 = assignments (SSO deep-link)
  if (showAssignments && (v === 'assignments' || v === 'assign' || v === '1')) {
    return SECTION_ASSIGNMENTS;
  }
  if (v === 'trainings' || v === 'training' || v === '2' || (!showAssignments && v === '1')) {
    return SECTION_TRAININGS;
  }
  if (v === 'certificates' || v === 'certs' || v === '0') return SECTION_CERTS;
  return SECTION_CERTS;
}

/**
 * Organised ground-staff training / certificates workspace.
 * - Certificates: profile certs + upload / renew
 * - Trainings: profile trainings + add / edit files
 * - Assignments: time-bound HR assign + completion certificate (suite on only)
 */
function StaffTrainingHub({
  employees = [],
  selfCrewId = null,
  showAssignments = false,
  assignmentProps = {},
  title = 'Staff training',
  subtitle = null
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [section, setSection] = useState(() =>
    sectionFromParam(searchParams.get('section') || searchParams.get('trainingSubTab'), showAssignments)
  );

  useEffect(() => {
    const next = sectionFromParam(
      searchParams.get('section') || searchParams.get('trainingSubTab'),
      showAssignments
    );
    setSection(next);
  }, [searchParams, showAssignments]);

  useEffect(() => {
    if (!showAssignments && section === SECTION_ASSIGNMENTS) {
      setSection(SECTION_CERTS);
    }
  }, [showAssignments, section]);

  const onSectionChange = (_, value) => {
    setSection(value);
    const next = new URLSearchParams(searchParams);
    const key =
      value === SECTION_TRAININGS
        ? 'trainings'
        : value === SECTION_ASSIGNMENTS
          ? 'assignments'
          : 'certificates';
    next.set('section', key);
    next.delete('trainingSubTab');
    setSearchParams(next, { replace: true });
  };

  return (
    <Box>
      <Stack spacing={0.75} mb={2.5}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B2A4A' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Manage profile certificates and trainings
            {showAssignments ? ', and assign time-bound trainings with optional certificate upload' : ''}.
          </Typography>
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={section}
          onChange={onSectionChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            bgcolor: 'grey.50',
            minHeight: 56,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 56,
              alignItems: 'center'
            }
          }}
        >
          <Tab
            icon={<WorkspacePremium sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Certificates"
          />
          <Tab
            icon={<School sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Trainings"
          />
          {showAssignments && (
            <Tab
              icon={<AssignmentInd sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Assignments"
            />
          )}
        </Tabs>

        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          {section === SECTION_CERTS && (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              Upload, renew, and track expiry for certificates on each employee&apos;s profile.
              Select a person below to add or replace certificate files.
            </Alert>
          )}
          {section === SECTION_TRAININGS && (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              Record ongoing / completed trainings on employee profiles, attach proof files, and
              set reminder dates before expiry.
            </Alert>
          )}
          {section === SECTION_ASSIGNMENTS && showAssignments && (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              HR assigns time-bound trainings to bands, groups, or people. Employees update progress
              and upload a completion certificate when required.
            </Alert>
          )}
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
          {section === SECTION_CERTS && (
            <CrewTrainingUpdatesPanel
              variant="staff"
              members={employees}
              selfCrewId={selfCrewId}
              sectionFocus="certificates"
            />
          )}
          {section === SECTION_TRAININGS && (
            <CrewTrainingUpdatesPanel
              variant="staff"
              members={employees}
              selfCrewId={selfCrewId}
              sectionFocus="trainings"
            />
          )}
          {section === SECTION_ASSIGNMENTS && showAssignments && (
            <TrainingPanel
              employees={employees}
              currentUserId={assignmentProps.currentUserId}
              canAssignTraining={Boolean(assignmentProps.canAssignTraining)}
              canAssign={Boolean(assignmentProps.canAssign)}
              department={assignmentProps.department || ''}
              from={assignmentProps.from}
              to={assignmentProps.to}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default StaffTrainingHub;
